import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as _ from 'lodash-es';
import {getReference} from '@/utils/analyze/getReadingQuestionInfo';
import type { IGenericObj, IGroupType, ILimitItem, IObjString, IPageDataBoxType, IhightLightInfo,
  IhightLightItem, IlistItem, IAnswerItem, IGetAnswerList, 
  IResult,
  IParamsType,
  IQuestionResult} from '@/utils/ctype';
import {UNANSWERED, UNANSWEREDWRAP} from '@/utils/contant';
import { parseDraftHighlights } from '@/utils/draftHighlights';
import { getExamRequestFailure, settleExamRequest, type ExamRequestFailure } from '@/utils/network/examRequest';
import { getReadingContent, getAnswerList, saveAnswerList, submitAnswer, getResult } from '@/api/reading';
import type { ExamRequestOptions } from '@/api/type';
import {renderHightLightPage, commonReducers} from './common';
import { createDraftSaveHealth, recordDraftSaveFailure, type DraftSaveHealth } from './draftSaveHealth';

export interface IListeningTopicListState {
  showNotePanel: boolean;
  pageDataAll: IPageDataBoxType[];
  pageDataCache: Array<IPageDataBoxType| undefined>;
  pageData: IPageDataBoxType | null;
  recordId: number;
  limits: ILimitItem[];
  answerAll: Array<IAnswerItem| null | undefined>;
  answerMap: IGenericObj<string[]>;
  displayAnswerMap: IGenericObj<string>;
  result: IQuestionResult[];
  resultMap: IGenericObj<string>;
  currentFocus: number;
  pageDataIndex: number;
  loadAnswered: boolean;
  highLightInfo: IhightLightInfo;
  surplus_time: number;
  is_confirm: number;
  referenceMap: IGenericObj<{content: string; payload: {number: number; question_id: number;};}>;
  aiHightLightInfo: IGenericObj<IhightLightItem>;
  showDragField: boolean;
  draftSyncedAt: number;
  draftSavePending: boolean;
  draftSaveRequestId?: string;
  draftSaveRequestRecordId?: number;
  draftSaveHealth: DraftSaveHealth;
}

const initialState: IListeningTopicListState = {
  showNotePanel: false,
  pageDataCache: [],
  pageDataAll: [],
  pageData: null,
  limits: [],
  answerAll: [],
  answerMap: {},
  displayAnswerMap: {},
  result: [],
  resultMap: {},
  currentFocus: 1,
  recordId: 34,
  pageDataIndex: 0,
  loadAnswered: false,
  highLightInfo: {},
  surplus_time: 60 * 60,
  is_confirm: 0,
  referenceMap: {},
  aiHightLightInfo: {},
  showDragField: false,
  draftSyncedAt: 0,
  draftSavePending: false,
  draftSaveHealth: createDraftSaveHealth(),
};

type ExamLoadRequest = {record_id: number; signal?: AbortSignal;};

export const getAnswerListAsync = createAsyncThunk<IGetAnswerList, ExamLoadRequest, {rejectValue: ExamRequestFailure;}>('reading/getAnswerList', async (params, {rejectWithValue, signal}) => {
  try {    
    const requestSignals = [params.signal, signal].filter((item): item is AbortSignal => Boolean(item));
    const res = await settleExamRequest<Awaited<ReturnType<typeof getAnswerList>>>(
      requestSignal => getAnswerList({record_id: params.record_id}, {signal: requestSignal}),
      undefined,
      requestSignals,
    );
    if (res.code !== 200) {
      return rejectWithValue(getExamRequestFailure({code: res.code}));
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(getExamRequestFailure(err));
  }
});

export const getReadingAsync = createAsyncThunk<IPageDataBoxType[], ExamLoadRequest, {rejectValue: ExamRequestFailure;}>('reading/getPageData', async (params, {rejectWithValue, signal}) => {
  try {
    const requestSignals = [params.signal, signal].filter((item): item is AbortSignal => Boolean(item));
    const res = await settleExamRequest<Awaited<ReturnType<typeof getReadingContent>>>(
      requestSignal => getReadingContent({record_id: params.record_id}, {signal: requestSignal}),
      undefined,
      requestSignals,
    );
    if (res.code !== 200) {
      return rejectWithValue(getExamRequestFailure({code: res.code}));
    }
    return res.data.list;
  } catch (err) {
    return rejectWithValue(getExamRequestFailure(err));
  }
});

export const saveAnswerListAsync = createAsyncThunk<{id: number; recordId: number;} | undefined, ExamRequestOptions | void, {rejectValue: ExamRequestFailure;}>(
  'reading/saveAnswerListAsync',
  async (params, {rejectWithValue, getState, signal}) => {
    try {
      const state: any = getState();
      const recordId = Number(state.reading.recordId);
      if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
        return rejectWithValue(getExamRequestFailure({name: 'ConditionError', code: 'EXAM_RECORD_MISMATCH'}));
      }
      const requestSignals = [params?.signal, signal].filter((item): item is AbortSignal => Boolean(item));
      const res = await settleExamRequest<Awaited<ReturnType<typeof saveAnswerList>>>((requestSignal: AbortSignal) => saveAnswerList({
        record_id: recordId,
        answer: state.reading.answerAll.filter((item: any) => item),
        mark: state.reading.highLightInfo ? JSON.stringify(state.reading.highLightInfo) : '',
        // mark: JSON.stringify({}),
        surplus_time: state.reading.surplus_time,
        is_confirm: state.reading.is_confirm
      }, {signal: requestSignal}), undefined, requestSignals);
      if (res.code !== 200) {
        return rejectWithValue(getExamRequestFailure({code: res.code}));
      }
      return {...res.data, recordId};
    } catch (err) {
      return rejectWithValue(getExamRequestFailure(err));
    }
  }, {
    condition: (_params, {getState}) => !(getState() as any).reading.draftSavePending,
  });

export const submitAnswerAsync = createAsyncThunk<unknown, ExamRequestOptions | void, {rejectValue: ExamRequestFailure;}>('listeningList/submitAnswer', async (params, {rejectWithValue, getState, signal}) => {
  try {
    const recordId = Number((getState() as any).reading.recordId);
    if (params?.recordId !== undefined && Number(params.recordId) !== recordId) {
      return rejectWithValue(getExamRequestFailure({name: 'ConditionError', code: 'EXAM_RECORD_MISMATCH'}));
    }
    const requestSignals = [params?.signal, signal].filter((item): item is AbortSignal => Boolean(item));
    const res = await settleExamRequest<Awaited<ReturnType<typeof submitAnswer>>>((requestSignal: AbortSignal) => submitAnswer({record_id: recordId}, {signal: requestSignal}), undefined, requestSignals);
    if (res.code !== 200) {
      return rejectWithValue(getExamRequestFailure({code: res.code}));
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(getExamRequestFailure(err));
  }
});


export const getReadingFeedbackAsync = createAsyncThunk<IResult | undefined, IParamsType>('reading/getListeningResult', async (params, { rejectWithValue }) => {
  try {
    const res = await getResult(params);
    if (res.code !== 200) {
      return rejectWithValue(null);
    }
    return res.data;
  } catch (err) {
    return rejectWithValue(null);
  }


});


export const readingSlice = createSlice({
  name: 'reading',
  initialState,
  reducers: {
    ...commonReducers,
    resetExamLoadState: (state) => {
      state.showNotePanel = false;
      state.pageDataAll = [];
      state.pageDataCache = [];
      state.pageData = null;
      state.limits = [];
      state.answerAll = [];
      state.answerMap = {};
      state.displayAnswerMap = {};
      state.result = [];
      state.resultMap = {};
      state.currentFocus = 1;
      state.pageDataIndex = 0;
      state.loadAnswered = false;
      state.highLightInfo = {};
      state.surplus_time = 60 * 60;
      state.is_confirm = 0;
      state.referenceMap = {};
      state.aiHightLightInfo = {};
      state.draftSyncedAt = 0;
      state.draftSavePending = false;
      state.draftSaveRequestId = undefined;
      state.draftSaveRequestRecordId = undefined;
      state.draftSaveHealth = createDraftSaveHealth(state.recordId);
    },
    saveAnswer: (state, {payload}: {payload: {index: number; value: IAnswerItem | null | undefined;} | {index: number[]; value: Array<IAnswerItem | null | undefined>;};}) => {

      
      const {index, value} = payload;
      const answerAll = state.answerAll.slice();
      const answerMap = Object.assign(state.answerMap);
      if (Array.isArray(index) && Array.isArray(value)) {
        index.forEach((indexItem, index2) => {
          answerAll[indexItem - 1] = value[index2];
          if (value[index2]) {
            const {group_id, id, type, answer} = value[index2] as IAnswerItem;
            answerMap[`${group_id}_${type}_${id ? id : ''}`] = answer;
          }
        });
        state.currentFocus = index[0];
      } else if (!Array.isArray(value)&& !Array.isArray(index)) {
        answerAll[index - 1] = value;
        if (value) {
          const {group_id, id, type, answer} = value;
          answerMap[`${group_id}_${type}_${id}`] = answer;
          // console.log(`${group_id}_${type}_${id}`, answer);
        } 
        
        state.currentFocus = index;
      }
      state.answerAll = answerAll;
      state.answerMap = answerMap;
      console.log('answerAll', answerMap, answerAll);
    },
    setAIHightLightInfo(state, {payload}) {
      state.aiHightLightInfo[state.pageDataIndex] = payload;
    },
    setShowDragFiled(state, {payload}) {
      state.showDragField = payload;
    }
  },
  extraReducers: (builder) => {

    const getIdToContentMap = (data: IGroupType) => {
      const {type, collect_option} = data;
      switch (type) {
        case 1:
        case 5: 
        case 3: 
        case 4:
        case 8: 
        case 11: 
        case 13: 
        case 12:
          return null;
        case 2:
        case 6:
        case 7:
        case 9:
        case 10:
          return collect_option.list.reduce((memo: IObjString, item) => {
            memo[item.id] = item.title || item.content;
            return memo;
          }, {});
      }
    };
    const getAnswerListHandle = (item: IlistItem, answer: string[], type: number, arr: Array<string | string[]| undefined | null>, idToContentMap: IObjString|undefined|null) => {
      const {number, option} = item;
      switch (type) {
        case 1: 
        case 5: 
          arr[number - 1] = option.find((item: {id:number;}) => String(item.id) === answer?.[0])?.title || UNANSWEREDWRAP ;
          break;
        case 7: 
          arr[number - 1] = (idToContentMap && idToContentMap[answer?.[0]]) || UNANSWEREDWRAP ;
          break;
        case 2: 
        case 6:
        case 9:
        case 10:  
          arr[number - 1] = (idToContentMap && idToContentMap[answer?.[0]]) || UNANSWERED;
          break;
        case 3:
          arr[number - 1] = answer?.[0] || UNANSWEREDWRAP;
          break;
        case 4: 
        case 8:
          if (item.is_multiple === 1) {
            arr[number - 1] = answer && answer.length > 0 ? answer : UNANSWERED;
            break;
          } else {
            arr[number - 1] = answer?.[0] || UNANSWERED;
            break;
          }
        case 13:
        case 11: 
        case 12:  
          arr[number - 1] = answer?.[0] || UNANSWERED;
          break;
      }
    };

    const getFeedback = (s: number) => {
      if (s === 1) {
        return 'success';
      }
      if (s === 2) {
        return 'fail';
      }
      return 'normal';
    };

    const dealAnwer = (state: IListeningTopicListState) => {
      const answerMap = state.answerMap;
      if (state.pageDataAll && state.loadAnswered) {

        console.log('answerMap', JSON.parse(JSON.stringify(state.answerMap)));
        const result: Array<IAnswerItem | null | undefined> = [];
        const answerList: string[] = [];
        
        state.pageDataAll.forEach((item1 => {
          item1.question.question.forEach((item2, index2: number) => {
            const {group_id, type} = item2;
            const isSingle = type !== 7;
            
            const idToContentMap = getIdToContentMap(item2);
            item2.list.forEach((item3, index3: number) => {
              const {id} = item3;

              const key = `${group_id}_${type}_${ isSingle? id : ''}`;
              const answer = answerMap[key];
              getAnswerListHandle(item3, answer, type, answerList, idToContentMap);
              if (isSingle) {
                result.push(answer ? {group_id, type, id, answer} : undefined);
                
                // answer.push();
              } else {
                if (answer && answer.length > index3) {
                  result.push( index3 === 0 ? {group_id, type, id, answer} : null) ;
                } else {
                  result.push(undefined);
                }
              }
            });
          });
          
          
        }));
        state.pageDataAll.forEach(item1 => {item1.answerAllText = answerList;});
        state.pageDataCache.forEach(item1 => {item1 && (item1.answerAllText = answerList);});
        console.log('获取接口处理结果', result, answerList);
        state.answerAll = result;

        // 显示高亮
        const curPageData = state.pageDataAll[state.pageDataIndex];
        const highLightObj = state.highLightInfo[state.pageDataIndex];
        if (curPageData && highLightObj) {
        
          const cloneCurPageData = _.cloneDeep(curPageData);
          renderHightLightPage(highLightObj, cloneCurPageData);
          const pageDataCache = [...state.pageDataCache];
          pageDataCache[state.pageDataIndex] = cloneCurPageData;
          state.pageDataCache = pageDataCache;
        }
      }
    };
    builder.addCase(getReadingAsync.fulfilled, (state, action) => {
      if (action.meta.arg.record_id !== state.recordId) return;
      const referenceMap:IGenericObj<{content: string; payload: {number: number; question_id: number;};}> = {};
      if (action.payload) {
        action.payload.forEach((item1) => {
          item1.question.question.forEach((item2)=> {
            if (item2.type === 7) {
              const l = item2.list.map((item3) => item3.number);
              item2.numbers = `${Math.min(...l)}-${Math.max(...l)}`;
            }
            if (item2.type === 3) {
              item2.optionLetters = item1.question.content.order_ary;
              item2.optionLettersHtml = _.cloneDeep(item1.question.content.order_ary);
            }
            item2.list.forEach((item3) => {
              item3.display_answerHtml = '(' + item3.display_answer + ')'; 
              referenceMap[item3.id] = getReference(item2, item3) || {};
            });
          });
          if (item1.question.content.order_ary) {
            item1.question.content.order_aryHtml = _.cloneDeep(item1.question.content.order_ary);
          }
        });
        state.referenceMap = referenceMap;
        state.pageDataAll = action.payload;
        console.log('pageDataAll', action.payload);
        state.pageDataIndex = 0;
        if (state.pageDataAll) {
          state.pageDataCache = [state.pageDataAll[state.pageDataIndex]];
          state.pageData = state.pageDataAll[state.pageDataIndex];
          let len = 0;
          state.limits = state.pageDataAll.map((item => {
            const itemQuestion =item.question; 
            len += itemQuestion.question_number.length;
            return {
              min: itemQuestion.question_number[0],
              max: itemQuestion.question_number[itemQuestion.question_number.length - 1]
            };
          }));
          state.answerAll = new Array(len);
        }
        dealAnwer(state);
      }
    }).addCase(getAnswerListAsync.fulfilled, (state, action) => {
      if (action.meta.arg.record_id !== state.recordId) return;
      state.loadAnswered = true;
      const data = action.payload;
      if (data) {
        state.draftSyncedAt = Date.now();
        if (action.meta.arg.record_id === state.recordId) state.draftSaveHealth = createDraftSaveHealth(state.recordId);
        const {answer, surplus_time, mark, is_confirm} = data;
        state.surplus_time = surplus_time || 60 * 60;
        state.highLightInfo = parseDraftHighlights(mark);
        state.is_confirm = is_confirm;
        console.log('高亮数据', state.highLightInfo);
        const answerMap: IGenericObj<string[]> = {};
        if (answer) {
          answer.map((item) => {
            const {group_id, id, type, answer} = item;
            answerMap[`${group_id}_${type}_${id ? id : ''}`] = answer;
          });
          state.answerMap = answerMap;
          dealAnwer(state);
        }
      }
      console.log('请求草稿接口', state.answerMap);
    }).addCase(getReadingFeedbackAsync.fulfilled, (state, action) => {
      if (action.payload?.list) {
        state.result = action.payload?.list;
        const resultMap: IGenericObj<string> = {};
        const displayAnswerMap: IGenericObj<string> = {};
        action.payload.list.forEach((item) => {
          const { question_id, result} = item;
          resultMap[question_id] = getFeedback(result);
          console.log(item.question_id, item.type, item.multiple_answer, item);
          if (item.is_multiple === 1 && item.multiple_answer && item.multiple_answer.length > 0) {
            displayAnswerMap[question_id] = '(' + item.multiple_answer[0].join(' ; ') + ')'; 
          }
        });
        state.resultMap = resultMap;
        state.displayAnswerMap = displayAnswerMap;
        dealAnwer(state);
      }
    })
      .addCase(saveAnswerListAsync.pending, (state, action) => {
        if (action.meta.arg?.recordId === undefined || Number(action.meta.arg.recordId) === state.recordId) {
          state.draftSavePending = true;
          state.draftSaveRequestId = action.meta.requestId;
          state.draftSaveRequestRecordId = state.recordId;
          if (state.draftSaveHealth.recordId !== state.recordId) state.draftSaveHealth = createDraftSaveHealth(state.recordId);
        }
      })
      .addCase(saveAnswerListAsync.fulfilled, (state, action) => {
        if (state.draftSaveRequestId !== action.meta.requestId) return;
        state.draftSavePending = false;
        state.draftSaveRequestId = undefined;
        state.draftSaveRequestRecordId = undefined;
        if (action.payload?.recordId === state.recordId) {
          state.draftSyncedAt = Date.now();
          state.draftSaveHealth = createDraftSaveHealth(state.recordId);
        }
      })
      .addCase(saveAnswerListAsync.rejected, (state, action) => {
        if (state.draftSaveRequestId !== action.meta.requestId) return;
        const isCurrentRecord = state.draftSaveRequestRecordId === state.recordId;
        state.draftSavePending = false;
        state.draftSaveRequestId = undefined;
        state.draftSaveRequestRecordId = undefined;
        if (isCurrentRecord && state.draftSaveHealth.recordId === state.recordId) recordDraftSaveFailure(state.draftSaveHealth, action.payload || getExamRequestFailure(action.error));
      })
      .addCase(submitAnswerAsync.fulfilled, () => {});
  },
});


export const {toggleShowNotePanel, setPanelDataByIndex, setFocusIndex, saveAnswer,
  saveHighlightData, hideDialog, showDialog, updateTimeState, updateRecordId,
  clearHightLightInfo, updatePanelInfo, deleteNote, setAIHightLightInfo, setShowDragFiled,
  resetExamLoadState} = readingSlice.actions;
